import { CellProps, Column } from "react-table";

import { EltSyncsQuery } from "@/apollo/types";
import { LoadIfInView } from "@/components/elements/DataTable";
import { FormatRelativeTime } from "@/components/elements/FormatRelativeTime";
import { FormatTime } from "@/components/elements/FormatTime";
import Tooltip from "@/components/elements/Tooltip";
import { EltSyncStatusBadge } from "@/features/elt";
import cronExpressionToDayjs from "@/helpers/cronExpressionToDayjs";
import { getCronInfo } from "@/hooks/useCron";
import { IntegrationLogo } from "@/integrations";
import { useDataWarehouseContext } from "@/providers/DataWarehouseProvider";

import { LastSuccessfulSyncCell } from "./cells/LastSuccessfulSyncCell";
import { SourceCell } from "./cells/SourceCell";
import { SyncMenu } from "./cells/SyncMenu";
import { ConnectorFilter } from "./filters/ConnectorFilter";
import { SyncStateFilter } from "./filters/SyncStateFilter";
import { cronExprSortFn } from "./sorting-fns";

export type DataSource = EltSyncsQuery["eltSyncs"][0];

export const columns: Column<DataSource>[] = [
  {
    id: "connector", // This is a hidden column used for filtering
    accessor: (row) => row.sourceConnectionId,
    Header: "Connector",
    Filter: ConnectorFilter,
  },
  {
    id: "source",
    accessor: (row) => row.sourceConnection?.label || row.sourceIntegrationId,
    Header: "Source",
    Cell: ({ row }: CellProps<DataSource>) => {
      return <SourceCell row={row.original} />;
    },
  },
  {
    id: "destination",
    accessor: "destinationSchemaName",
    Header: DestinationHeader,
    Cell: ({
      value,
    }: CellProps<DataSource, DataSource["destinationSchemaName"]>) => {
      return <div className="max-w-lg truncate whitespace-nowrap">{value}</div>;
    },
  },
  {
    id: "nextSync",
    Header: "Next sync",
    Cell: ({
      row,
      value: syncInterval,
    }: CellProps<DataSource, DataSource["syncInterval"]>) => {
      if (
        row.original.status === "NOT_STARTED" ||
        row.original.status === "PAUSED"
      ) {
        return <span className="text-muted-foreground">-</span>;
      }

      const cronExpression = row.original.orchestrationWorkflow?.id
        ? row.original.orchestrationWorkflow.cronExpression
        : syncInterval;

      if (cronExpression) {
        const date = cronExpressionToDayjs(cronExpression);
        const cronInfo = getCronInfo(cronExpression);
        return (
          <Tooltip
            content={
              <>
                <FormatTime date={date} format="LLL" /> -{" "}
                <span>{cronInfo.info}</span>
              </>
            }
          >
            <FormatRelativeTime date={date} className="mb-1 block" />
          </Tooltip>
        );
      }
      return null;
    },
    accessor: "syncInterval",
    sortType: cronExprSortFn,
  },
  {
    id: "lastSuccessfulSync",
    Header: "Last successful sync",
    Cell: ({ row }: CellProps<DataSource, DataSource["id"]>) => {
      return (
        <LoadIfInView>
          <LastSuccessfulSyncCell syncId={row.original.id} />
        </LoadIfInView>
      );
    },
    disableSortBy: true,
  },
  {
    accessor: "status",
    Header: "Status",
    Cell: ({ row }: CellProps<DataSource, DataSource["id"]>) => {
      return (
        <LoadIfInView>
          <EltSyncStatusBadge eltSyncId={row.original.id} />
        </LoadIfInView>
      );
    },
    width: 150,
    maxWidth: 150,
    disableSortBy: true,
  },
  {
    id: "syncState",
    accessor: "status",
    Header: "Sync State",
    Cell: ({ row }: CellProps<DataSource, DataSource["id"]>) => {
      return (
        <LoadIfInView>
          <EltSyncStatusBadge eltSyncId={row.original.id} />
        </LoadIfInView>
      );
    },
    filter: "syncState",
    Filter: SyncStateFilter,
  },
  {
    Header: "",
    Cell: ({ row, value: id }: CellProps<DataSource, DataSource["id"]>) => (
      <div className="flex justify-end">
        <SyncMenu
          syncId={id}
          active={row.original.active}
          sourceId={row.original.sourceConnection?.id ?? ""}
        />
      </div>
    ),
    width: 45,
    maxWidth: 45,
    accessor: "id",
    disableSortBy: true,
  },
  {
    accessor: "createdAt",
  },
  {
    accessor: "active",
    filter: "exact",
  },
];

function DestinationHeader() {
  const { integration } = useDataWarehouseContext();
  return (
    <Tooltip content={`Destination schema name in ${integration.name}`}>
      <div className="inline-flex items-center gap-1.5">
        <IntegrationLogo id={integration.id} className="h-4 w-4" /> Schema Name
      </div>
    </Tooltip>
  );
}
